/**
 * Tournament JSON export / import / clone utilities.
 *
 * Export  – serialises a tournament (settings + teams + matches + sets + rallies)
 *           to a JSON file the browser downloads.
 * Import  – reads that JSON, matches teams by name to existing DB teams, then
 *           recreates everything under a new tournament record.
 * Clone   – does the same without a file, using the existing DB data directly.
 */

import { supabase, SERVER_FAULT_PLAYER_ID, getParticipatingTeams, getTeams } from './supabase';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TJRally {
  rallyNumber: number;
  servingTeamId: string;    // ref to team.id in this export
  serverId: string;         // ref to player.id in this export, or SERVER_FAULT_PLAYER_ID
  pointWonById: string;     // ref to team.id
  lostReason: string | null;
  pointWinnerId: string | null; // ref to player.id or null
  categories: Array<{ playerId: string; category: string }>;
  isServerFault: boolean;
}

export interface TJSet {
  setNumber: number;
  scoreA: number;
  scoreB: number;
  winnerId: string | null;  // ref to team.id
  startedAt: string;
  endedAt: string | null;
  sideChangeShown: boolean;
  rallies: TJRally[];
}

export interface TJMatch {
  id: string;               // original ID (for cross-reference only)
  matchNumber: number;
  date: string;
  matchTime: string | null;
  matchType: 'league' | 'playoff';
  teamAId: string;          // ref to team.id
  teamBId: string;
  tossWonById: string | null;
  tossChoice: string | null;
  status: string;
  winnerId: string | null;
  winningPoint: number | null;
  goldenPoint: number | null;
  serveRotationCooloff: number;
  startTime: string | null;
  endTime: string | null;
  sets: TJSet[];
}

export interface TJTeam {
  id: string;               // original ID (for cross-reference only)
  name: string;
  shortName: string;
  color: string;
  players: Array<{ id: string; name: string; number: number }>;
}

export interface TournamentJSON {
  version: '1.0';
  exportedAt: string;
  tournament: {
    name: string;
    startDate: string;
    endDate: string;
    venue: string | null;
    leagueWinningPoint: number;
    leagueGoldenPoint: number;
    playoffWinningPoint: number;
    playoffGoldenPoint: number;
  };
  teams: TJTeam[];
  matches: TJMatch[];
}

// ── Export ────────────────────────────────────────────────────────────────────

export async function exportTournamentAsJSON(tournamentId: string): Promise<void> {
  // 1. Fetch tournament settings
  const { data: t, error: te } = await supabase
    .from('tournaments')
    .select('*')
    .eq('id', tournamentId)
    .single();
  if (te || !t) throw new Error('Failed to fetch tournament');

  // 2. Fetch participating teams with players
  const participatingTeams = await getParticipatingTeams(tournamentId);
  const teams: TJTeam[] = participatingTeams.map((team: any) => ({
    id: team.id,
    name: team.name,
    shortName: team.shortName,
    color: team.color,
    players: (team.players || []).map((p: any) => ({
      id: p.id,
      name: p.name,
      number: p.number,
    })),
  }));

  // 3. Fetch matches + sets + rallies (full data)
  const { data: rawMatches, error: me } = await supabase
    .from('matches')
    .select(`
      id, match_number, date, match_time, match_type,
      team_a_id, team_b_id, toss_won_by_id, toss_choice,
      status, winner_id, winning_point, golden_point,
      serve_rotation_cooloff, start_time, end_time,
      sets (
        set_number, score_a, score_b, winner_id,
        started_at, ended_at, side_change_shown,
        rallies (
          rally_number, serving_team_id, server_id, point_won_by_id,
          lost_reason, point_winner_id, categories, is_server_fault
        )
      )
    `)
    .eq('tournament_id', tournamentId)
    .neq('status', 'discarded')
    .order('match_number');
  if (me) throw new Error('Failed to fetch matches');

  const matches: TJMatch[] = (rawMatches ?? []).map((m: any) => ({
    id: m.id,
    matchNumber: m.match_number,
    date: m.date,
    matchTime: m.match_time ?? null,
    matchType: m.match_type,
    teamAId: m.team_a_id,
    teamBId: m.team_b_id,
    tossWonById: m.toss_won_by_id ?? null,
    tossChoice: m.toss_choice ?? null,
    status: m.status,
    winnerId: m.winner_id ?? null,
    winningPoint: m.winning_point ?? null,
    goldenPoint: m.golden_point ?? null,
    serveRotationCooloff: m.serve_rotation_cooloff ?? 5,
    startTime: m.start_time ?? null,
    endTime: m.end_time ?? null,
    sets: (m.sets ?? [])
      .sort((a: any, b: any) => a.set_number - b.set_number)
      .map((s: any) => ({
        setNumber: s.set_number,
        scoreA: s.score_a,
        scoreB: s.score_b,
        winnerId: s.winner_id ?? null,
        startedAt: s.started_at,
        endedAt: s.ended_at ?? null,
        sideChangeShown: s.side_change_shown ?? false,
        rallies: (s.rallies ?? [])
          .sort((a: any, b: any) => a.rally_number - b.rally_number)
          .map((r: any) => ({
            rallyNumber: r.rally_number,
            servingTeamId: r.serving_team_id,
            serverId: r.server_id,
            pointWonById: r.point_won_by_id,
            lostReason: r.lost_reason ?? null,
            pointWinnerId: r.point_winner_id ?? null,
            categories: r.categories ?? [],
            isServerFault: r.is_server_fault ?? false,
          })),
      })),
  }));

  const payload: TournamentJSON = {
    version: '1.0',
    exportedAt: new Date().toISOString(),
    tournament: {
      name: t.name,
      startDate: t.start_date,
      endDate: t.end_date,
      venue: t.venue ?? null,
      leagueWinningPoint: t.league_winning_point,
      leagueGoldenPoint: t.league_golden_point,
      playoffWinningPoint: t.playoff_winning_point,
      playoffGoldenPoint: t.playoff_golden_point,
    },
    teams,
    matches,
  };

  // 4. Trigger browser download
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `tournament-${t.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Shared import/clone core ───────────────────────────────────────────────────

/**
 * Builds a broadcast-ID string in VB### format, starting after the current max.
 * Fetches the current max once and increments for each call using a closure counter.
 */
async function makeBroadcastIdGenerator(): Promise<() => string> {
  const { data } = await supabase
    .from('matches')
    .select('broadcast_id')
    .not('broadcast_id', 'is', null)
    .order('broadcast_id', { ascending: false })
    .limit(1);
  const lastSeq = data?.[0]?.broadcast_id
    ? parseInt((data[0].broadcast_id as string).replace('VB', ''), 10)
    : 0;
  let counter = lastSeq;
  return () => `VB${String(++counter).padStart(3, '0')}`;
}

interface ImportOptions {
  data: TournamentJSON;
  newName: string;
  newStartDate: string;
  newEndDate: string;
  /** Pre-built map: export teamId → existing DB teamId */
  teamIdMap: Map<string, string>;
  /** Pre-built map: export playerId → existing DB playerId. SERVER_FAULT is handled automatically. */
  playerIdMap: Map<string, string>;
}

async function runImportCore(opts: ImportOptions): Promise<string> {
  const { data, newName, newStartDate, newEndDate, teamIdMap, playerIdMap } = opts;

  const remapTeam = (id: string | null) => (id ? (teamIdMap.get(id) ?? id) : null);
  const remapPlayer = (id: string | null) => {
    if (!id) return null;
    if (id === SERVER_FAULT_PLAYER_ID) return SERVER_FAULT_PLAYER_ID;
    return playerIdMap.get(id) ?? id;
  };

  // 1. Create tournament
  const { data: newT, error: te } = await supabase
    .from('tournaments')
    .insert({
      name: newName,
      start_date: newStartDate,
      end_date: newEndDate,
      venue: data.tournament.venue,
      league_winning_point: data.tournament.leagueWinningPoint,
      league_golden_point: data.tournament.leagueGoldenPoint,
      playoff_winning_point: data.tournament.playoffWinningPoint,
      playoff_golden_point: data.tournament.playoffGoldenPoint,
    })
    .select('id')
    .single();
  if (te || !newT) throw new Error('Failed to create tournament');
  const newTournamentId: string = newT.id;

  // 2. Add participating teams
  const teamIds = [...new Set([...teamIdMap.values()])];
  for (const teamId of teamIds) {
    await supabase
      .from('tournament_teams')
      .insert({ tournament_id: newTournamentId, team_id: teamId })
      .then(({ error }) => { if (error) console.error('Failed to add team to tournament:', error); });
  }

  // 3. Broadcast ID generator (batch-safe)
  const nextBroadcastId = await makeBroadcastIdGenerator();

  // 4. For each match: create match → sets → rallies
  for (const match of data.matches) {
    const newTeamAId = remapTeam(match.teamAId);
    const newTeamBId = remapTeam(match.teamBId);
    if (!newTeamAId || !newTeamBId) continue; // skip if team not mapped

    const { data: newMatch, error: mErr } = await supabase
      .from('matches')
      .insert({
        tournament_id: newTournamentId,
        match_number: match.matchNumber,
        date: match.date,
        match_time: match.matchTime,
        match_type: match.matchType,
        team_a_id: newTeamAId,
        team_b_id: newTeamBId,
        toss_won_by_id: remapTeam(match.tossWonById),
        toss_choice: match.tossChoice,
        status: match.status,
        winner_id: remapTeam(match.winnerId),
        winning_point: match.winningPoint,
        golden_point: match.goldenPoint,
        serve_rotation_cooloff: match.serveRotationCooloff,
        start_time: match.startTime,
        end_time: match.endTime,
        broadcast_id: nextBroadcastId(),
      })
      .select('id')
      .single();
    if (mErr || !newMatch) { console.error('Failed to create match:', mErr); continue; }
    const newMatchId: string = newMatch.id;

    for (const set of match.sets) {
      const { data: newSet, error: sErr } = await supabase
        .from('sets')
        .insert({
          match_id: newMatchId,
          set_number: set.setNumber,
          score_a: set.scoreA,
          score_b: set.scoreB,
          winner_id: remapTeam(set.winnerId),
          started_at: set.startedAt,
          ended_at: set.endedAt,
          side_change_shown: set.sideChangeShown,
        })
        .select('id')
        .single();
      if (sErr || !newSet) { console.error('Failed to create set:', sErr); continue; }
      const newSetId: string = newSet.id;

      if (set.rallies.length === 0) continue;

      // Batch-insert rallies in chunks of 100
      const CHUNK = 100;
      for (let i = 0; i < set.rallies.length; i += CHUNK) {
        const chunk = set.rallies.slice(i, i + CHUNK).map(r => ({
          set_id: newSetId,
          rally_number: r.rallyNumber,
          serving_team_id: remapTeam(r.servingTeamId) ?? newTeamAId,
          server_id: remapPlayer(r.serverId) ?? SERVER_FAULT_PLAYER_ID,
          point_won_by_id: remapTeam(r.pointWonById) ?? newTeamAId,
          lost_reason: r.lostReason,
          point_winner_id: remapPlayer(r.pointWinnerId),
          categories: r.categories.map(c => ({
            playerId: remapPlayer(c.playerId) ?? c.playerId,
            category: c.category,
          })),
          is_server_fault: r.isServerFault,
        }));
        const { error: rErr } = await supabase.from('rallies').insert(chunk);
        if (rErr) console.error('Failed to insert rallies chunk:', rErr);
      }
    }
  }

  return newTournamentId;
}

// ── Import from JSON ───────────────────────────────────────────────────────────

export interface ImportResult {
  tournamentId: string;
  unmatchedTeams: string[];    // team names from JSON that had no DB match
}

export async function importTournamentFromJSON(
  data: TournamentJSON,
  newName: string,
  newStartDate: string,
  newEndDate: string,
): Promise<ImportResult> {
  // Load all DB teams
  const allTeams = await getTeams();

  // Build name → id map (case-insensitive)
  const dbTeamByName = new Map<string, string>();
  for (const t of allTeams) {
    dbTeamByName.set((t as any).name.toLowerCase().trim(), (t as any).id);
  }

  // Build player name maps per team (name → id)
  const dbPlayerByTeamAndName = new Map<string, string>();
  for (const t of allTeams) {
    for (const p of (t as any).players ?? []) {
      dbPlayerByTeamAndName.set(`${(t as any).id}::${p.name.toLowerCase().trim()}`, p.id);
    }
  }

  const teamIdMap = new Map<string, string>();
  const playerIdMap = new Map<string, string>();
  const unmatchedTeams: string[] = [];

  for (const exportTeam of data.teams) {
    const dbId = dbTeamByName.get(exportTeam.name.toLowerCase().trim());
    if (!dbId) {
      unmatchedTeams.push(exportTeam.name);
      continue;
    }
    teamIdMap.set(exportTeam.id, dbId);

    // Map players by name within the matched team
    for (const exportPlayer of exportTeam.players) {
      const key = `${dbId}::${exportPlayer.name.toLowerCase().trim()}`;
      const dbPlayerId = dbPlayerByTeamAndName.get(key);
      if (dbPlayerId) {
        playerIdMap.set(exportPlayer.id, dbPlayerId);
      }
      // Unmatched players fall back to the original ID; the DB FK will reject if truly unknown.
      // In practice this only happens if players were renamed since the export.
    }
  }

  const tournamentId = await runImportCore({
    data,
    newName,
    newStartDate,
    newEndDate,
    teamIdMap,
    playerIdMap,
  });

  return { tournamentId, unmatchedTeams };
}

// ── Clone ──────────────────────────────────────────────────────────────────────

export async function cloneTournament(
  sourceTournamentId: string,
  newName: string,
  newStartDate: string,
  newEndDate: string,
): Promise<string> {
  // Build in-memory export of source tournament
  const { data: t, error: te } = await supabase
    .from('tournaments').select('*').eq('id', sourceTournamentId).single();
  if (te || !t) throw new Error('Failed to fetch tournament');

  const participatingTeams = await getParticipatingTeams(sourceTournamentId);
  const teams: TJTeam[] = participatingTeams.map((team: any) => ({
    id: team.id, name: team.name, shortName: team.shortName, color: team.color,
    players: (team.players || []).map((p: any) => ({ id: p.id, name: p.name, number: p.number })),
  }));

  const { data: rawMatches } = await supabase
    .from('matches')
    .select(`
      id, match_number, date, match_time, match_type,
      team_a_id, team_b_id, toss_won_by_id, toss_choice,
      status, winner_id, winning_point, golden_point,
      serve_rotation_cooloff, start_time, end_time,
      sets (
        set_number, score_a, score_b, winner_id,
        started_at, ended_at, side_change_shown,
        rallies (
          rally_number, serving_team_id, server_id, point_won_by_id,
          lost_reason, point_winner_id, categories, is_server_fault
        )
      )
    `)
    .eq('tournament_id', sourceTournamentId)
    .neq('status', 'discarded')
    .order('match_number');

  const matches: TJMatch[] = (rawMatches ?? []).map((m: any) => ({
    id: m.id, matchNumber: m.match_number, date: m.date, matchTime: m.match_time ?? null,
    matchType: m.match_type, teamAId: m.team_a_id, teamBId: m.team_b_id,
    tossWonById: m.toss_won_by_id ?? null, tossChoice: m.toss_choice ?? null,
    status: m.status, winnerId: m.winner_id ?? null, winningPoint: m.winning_point ?? null,
    goldenPoint: m.golden_point ?? null, serveRotationCooloff: m.serve_rotation_cooloff ?? 5,
    startTime: m.start_time ?? null, endTime: m.end_time ?? null,
    sets: (m.sets ?? []).sort((a: any, b: any) => a.set_number - b.set_number).map((s: any) => ({
      setNumber: s.set_number, scoreA: s.score_a, scoreB: s.score_b,
      winnerId: s.winner_id ?? null, startedAt: s.started_at, endedAt: s.ended_at ?? null,
      sideChangeShown: s.side_change_shown ?? false,
      rallies: (s.rallies ?? []).sort((a: any, b: any) => a.rally_number - b.rally_number).map((r: any) => ({
        rallyNumber: r.rally_number, servingTeamId: r.serving_team_id, serverId: r.server_id,
        pointWonById: r.point_won_by_id, lostReason: r.lost_reason ?? null,
        pointWinnerId: r.point_winner_id ?? null, categories: r.categories ?? [],
        isServerFault: r.is_server_fault ?? false,
      })),
    })),
  }));

  const payload: TournamentJSON = {
    version: '1.0', exportedAt: new Date().toISOString(),
    tournament: {
      name: t.name, startDate: t.start_date, endDate: t.end_date, venue: t.venue ?? null,
      leagueWinningPoint: t.league_winning_point, leagueGoldenPoint: t.league_golden_point,
      playoffWinningPoint: t.playoff_winning_point, playoffGoldenPoint: t.playoff_golden_point,
    },
    teams, matches,
  };

  // For clone, IDs are already correct — identity map
  const teamIdMap = new Map<string, string>(teams.map(team => [team.id, team.id]));
  const playerIdMap = new Map<string, string>();
  for (const team of teams) {
    for (const p of team.players) playerIdMap.set(p.id, p.id);
  }

  return runImportCore({ data: payload, newName, newStartDate, newEndDate, teamIdMap, playerIdMap });
}
